import _, {isString, noop} from 'lodash';

import {deleteRawBootCacheAsync} from '../../../srcCommon/services/boot/rawBootCache';
import {resetOfflineResources} from '../../../srcCommon/services/offline/offlineResources';
import {getRecipientParser} from '../../helpers/models/recipientParsers';
import {ContactSourcesEnum, isContactSourcesEnum} from '../../models/api/contactModel';
import {RecipientRolesEnum} from '../../models/api/recipientModel';
import {LegacyFrameComposeDataModel} from '../../models/frames/legacyFrameMethodsModel';
import {clearElectronCache} from '../../services/electron/electronBridgeOutbound';
import {ThunkAction} from '../../store/appThunk';
import {composeMessageAction} from '../../store/data/messages/draftMessagesActions';
import {getHasOfflineCache} from '../../store/featureFlags/featureFlagsSelectors';
import {pushDefaultContentTypeAction} from '../../store/panels/app/appPanelRouteActions';
import {AppPanelId} from '../../store/panels/panelStateBase';
import {navigateToLinkAction} from '../../store/stateless/navigationActions';

export function composeLegacyFrameAction(data: LegacyFrameComposeDataModel): ThunkAction {
  return (dispatch) => {
    const {to, subject, source, forcePopout} = data;

    // Handling recipients.
    const contactSource = source && isContactSourcesEnum(source) ? source : ContactSourcesEnum.EMAIL;
    const recipients = parseToRecipients(to, contactSource);

    // Starting composer from parsed options.
    dispatch(
      composeMessageAction({
        recipients,
        subject,
        shouldForcePopout: Boolean(forcePopout),
      }),
    );
  };
}

function parseToRecipients(toRecipients: ReadonlyArray<string>, source: ContactSourcesEnum) {
  const parser = getRecipientParser(source);
  if (!parser) {
    return undefined;
  }

  return _(toRecipients)
    .filter(isString)
    .compact()
    .map((r) => parser(RecipientRolesEnum.TO, r))
    .compact()
    .value();
}

export function navigateLegacyFrameAction(panelId: AppPanelId, path: string) {
  // If we're navigating to the default path, check for a location to restore.
  if (path === '/inboxes') {
    return pushDefaultContentTypeAction(panelId);
  }

  // Otherwise, use the specified path.
  return navigateToLinkAction(path);
}

export function openUrlLegacyFrameAction(url: string): ThunkAction {
  return (dispatch) => dispatch(navigateToLinkAction(url));
}

export function resetOfflineCacheLegacyFrameAction(): ThunkAction {
  return async (_dispatch, getState) => {
    const hasOfflineCache = getHasOfflineCache(getState());

    await Promise.all([
      resetOfflineResources({shouldSetup: hasOfflineCache}).catch(noop),
      deleteRawBootCacheAsync().catch(noop),
      clearElectronCache().catch(noop),
    ]);
  };
}
